import { IQuery, useFirebaseData } from "../../@shared/hooks/useFirebaseData";
import { Collections } from "../../@core/constants/FirebasePaths";
import { IUser } from "@core/interfaces/IUser";
import { functions } from "../../firebase/config";

export const useUsers = ({ loadData = false, queries = [] }: { loadData?: boolean, queries?: IQuery[] }) => {
  const db = useFirebaseData<IUser>({
    collectionPath: Collections.users._path,
    loadData,
    queries: [...queries]
  });

  const createAccount = async (uid: string, data: Partial<IUser>) => {
    const account = await db.docWithId(Collections.users._path + `/${uid}`);
    if (!account) await db.set(uid, data);
  };

  const getUser = async (uid: string): Promise<IUser> => {
    return await db.docWithId(Collections.users._path + `/${uid}`);
  };

  const updateUser = async (uid: string, data: Partial<IUser>) => {
    await db.update(uid, data);
  };

  const updateUserAuthStatus = async (uid: string, isDisabled: boolean) => {
    try {
      const updateAuthStatusFunction = isDisabled
        ? functions.httpsCallable(`api/user/activate/${uid}`)
        : functions.httpsCallable(`api/user/disable/${uid}`);
      await updateAuthStatusFunction();
    } catch (error) {
      console.error("Error updating user auth status:", error);
      throw error;
    }
  };

  const toggleFavoriteService = async (uid: string, serviceId: string) => {
    const user = await db.docWithId(`${Collections.users._path}/${uid}`) as IUser;
    if (!user) return;

    const favorites = user?.favorites || [];
    const currentFavoriteServiceIndex = favorites.findIndex((favorite) => favorite.id === serviceId);

    if (currentFavoriteServiceIndex !== -1) {
      favorites.splice(currentFavoriteServiceIndex, 1);
      await db.update(uid, { favorites });
      await db.set(
        uid,
        { trash: false },
        `${Collections.services._path}/${serviceId}/${Collections.services.favorites}`
      );
      return;
    }
    favorites.push(db.getReference(`${Collections.services._path}/${serviceId}`));
    await db.set(
      uid,
      { trash: true, deletedAt: Date.now() },
      `${Collections.services._path}/${serviceId}/${Collections.services.favorites}`
    );
    await db.update(uid, { favorites });
  };

  return {
    createAccount,
    getUser,
    docListener: db.docListener,
    subscribe: db.listenDocument,
    updateUser,
    toggleFavoriteService,
    users: db.data,
    updateUserAuthStatus
  };
};
